/**
 * Trend Tracker hooks.
 *
 * Loads data in this order:
 *   1. Live fixture at /.trends-fixture.json — generated by bin/generate-trends.cjs
 *      (pipeline: SerpAPI Google Trends + datalake recent-team-mentions + Gemini
 *      synthesis). Refreshed manually until the backend Trend Engine ships.
 *   2. Hardcoded sample trends from src/data/trendFixture.ts — used when the live
 *      fixture is missing or empty, so the UI never shows an empty Radar.
 *
 * When the real /v2/trends/feed endpoint ships, swap the network leg of `queryFn`
 * for that fetch — everything downstream stays the same.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TREND_FIXTURE } from '@/data/trendFixture';
import {
  scoreOpportunityOrder,
  trendMatchesGeo,
  trendMatchesQuery,
  type Trend,
  type TrendCategory,
  type TrendRankings,
  type TrendStatus,
} from '@/utils/trend';

export type TrendDataSource = 'live' | 'sample';

export interface TrendFeedMetadata {
  source: TrendDataSource;
  /** Generation timestamp from the live fixture, if present. ISO-8601. */
  generatedAt?: string | null;
  /** Names of the pipeline components that produced the live fixture. */
  pipelineSources?: string[];
}

interface TrendsFeedPayload {
  trends: Trend[];
  rankings: TrendRankings | null;
  metadata: TrendFeedMetadata;
}

const EMPTY_RANKINGS: TrendRankings = {
  tiktok_hashtags: [],
  google_trends_now: [],
  google_news: [],
  youtube: [],
  reddit: [],
};

const LIVE_FIXTURE_URL = '/.trends-fixture.json';
const SAMPLE_METADATA: TrendFeedMetadata = { source: 'sample' };

async function loadLiveFixture(): Promise<TrendsFeedPayload | null> {
  try {
    const res = await fetch(LIVE_FIXTURE_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as {
      generated_at?: string;
      sources?: string[];
      trends?: Trend[];
      rankings?: TrendRankings;
    };
    const trends = Array.isArray(json.trends) ? json.trends : [];
    if (trends.length === 0) return null;
    return {
      trends,
      rankings: json.rankings ?? null,
      metadata: {
        source: 'live',
        generatedAt: json.generated_at ?? null,
        pipelineSources: json.sources ?? [],
      },
    };
  } catch {
    return null;
  }
}

async function fetchTrendsFeed(): Promise<TrendsFeedPayload> {
  const live = await loadLiveFixture();
  if (live) return live;
  return { trends: TREND_FIXTURE, rankings: null, metadata: SAMPLE_METADATA };
}

export interface UseTrendsParams {
  category?: TrendCategory | 'all';
  geo?: string | 'all';
  status?: TrendStatus | 'all';
  /** Free-text search — tokenised AND-match over title, description, hashtags,
   *  keywords, and signal metadata. Empty string disables the filter. */
  query?: string;
  limit?: number;
  enabled?: boolean;
}

export function useTrends({
  category = 'all',
  geo = 'all',
  status = 'all',
  query = '',
  limit = 20,
  enabled = true,
}: UseTrendsParams = {}) {
  const { data, isLoading, isError, error } = useQuery<TrendsFeedPayload>({
    queryKey: ['trends', 'feed'],
    enabled,
    queryFn: fetchTrendsFeed,
    staleTime: 5 * 60 * 1000,
  });

  const filtered = useMemo(() => {
    const all = data?.trends ?? [];
    return all
      .filter((t) => (category === 'all' ? true : t.category === category))
      .filter((t) => (status === 'all' ? true : t.status === status))
      .filter((t) => trendMatchesGeo(t, geo))
      .filter((t) => trendMatchesQuery(t, query))
      .filter((t) => !t.relevance.dismissed)
      .sort(scoreOpportunityOrder)
      .slice(0, limit);
  }, [data, category, geo, status, query, limit]);

  return {
    data: filtered,
    raw: data?.trends,
    rankings: data?.rankings ?? EMPTY_RANKINGS,
    metadata: data?.metadata ?? SAMPLE_METADATA,
    isLoading,
    isError,
    error,
  };
}

/**
 * Rankings-only hook — same query key as useTrends so the fixture is fetched
 * once and both pages share cache. Returns the raw top-lists plus the same
 * feed metadata so the "Trend Rankings" page can also render the Live-pill.
 */
export function useTrendRankings() {
  const { data, isLoading, isError, error } = useQuery<TrendsFeedPayload>({
    queryKey: ['trends', 'feed'],
    queryFn: fetchTrendsFeed,
    staleTime: 5 * 60 * 1000,
  });
  return {
    rankings: data?.rankings ?? EMPTY_RANKINGS,
    metadata: data?.metadata ?? SAMPLE_METADATA,
    isLoading,
    isError,
    error,
  };
}

export function useTrend(trendId: string | undefined) {
  const { data: feed, isLoading, isError, error } = useQuery<TrendsFeedPayload>({
    queryKey: ['trends', 'feed'],
    enabled: Boolean(trendId),
    queryFn: fetchTrendsFeed,
    staleTime: 5 * 60 * 1000,
  });
  const trend = useMemo(() => {
    if (!feed || !trendId) return null;
    return feed.trends.find((t) => t.id === trendId || t.slug === trendId) ?? null;
  }, [feed, trendId]);
  return { data: trend, isLoading, isError, error };
}

/** Top-N trends for the dashboard widget. Mirrors useTrends' filter chain. */
export function useTrendsForYou(limit = 3) {
  const { data, isLoading, metadata } = useTrends({ limit });
  return { data, isLoading, metadata };
}
